import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import {
  useSearchRadarReviewCnCode,
  useSearchRadarReviewText,
} from '@/shared/hooks/api/radarReview'
import { partitionRadarReviewItems } from '../utils/partitionResults'

type SearchMode = 'text' | 'cnCode'

const DEFAULT_RETRIEVAL_FLOOR = 0.1
const DEFAULT_RELEVANCE_THRESHOLD = 0.15
const DEFAULT_VECTOR_WEIGHT = 0.6
const DEFAULT_TOP_K = 200
const REVIEW_SCORE_MIN = 0.1

export default function RadarReviewPage() {
  const navigate = useNavigate()
  const { data: session } = useGetUserSession()
  const textSearch = useSearchRadarReviewText()
  const cnSearch = useSearchRadarReviewCnCode()

  const [mode, setMode] = useState<SearchMode>('text')
  const [query, setQuery] = useState('')
  const [retrievalFloor, setRetrievalFloor] = useState(DEFAULT_RETRIEVAL_FLOOR)
  const [relevanceThreshold, setRelevanceThreshold] = useState(
    DEFAULT_RELEVANCE_THRESHOLD
  )
  const [vectorWeight, setVectorWeight] = useState(DEFAULT_VECTOR_WEIGHT)
  const [topK, setTopK] = useState(DEFAULT_TOP_K)
  const [automotiveDiagnostics, setAutomotiveDiagnostics] = useState(false)
  const [relevantOpen, setRelevantOpen] = useState(true)
  const [fullOpen, setFullOpen] = useState(true)
  const [retrievalExplainOpen, setRetrievalExplainOpen] = useState(false)

  useEffect(() => {
    if (session && !session.isArtemAdmin) {
      navigate('/', { replace: true })
    }
  }, [navigate, session])

  const isLoading = textSearch.isPending || cnSearch.isPending
  const textResult = textSearch.data
  const cnResult = cnSearch.data
  const activeError = textSearch.error ?? cnSearch.error

  const relevantItems = useMemo(() => {
    if (!textResult) {
      return []
    }
    return partitionRadarReviewItems(
      textResult.items,
      textResult.relevanceThreshold
    ).relevant
  }, [textResult])

  const onSearch = () => {
    const trimmed = query.trim()
    if (!trimmed) {
      return
    }
    if (mode === 'text') {
      textSearch.mutate({
        queryText: trimmed,
        relevanceThreshold,
        minCombinedScore: retrievalFloor,
        limit: topK,
        vectorWeight,
        lexicalWeight: 1 - vectorWeight,
        automotiveDiagnostics,
      })
      return
    }
    cnSearch.mutate({ cncode: trimmed })
  }

  if (session && !session.isArtemAdmin) {
    return null
  }

  return (
    <div className='mx-auto w-full max-w-7xl p-6 space-y-6'>
      <header className='space-y-2'>
        <h1 className='text-2xl font-semibold'>Radar Review</h1>
        <p className='text-sm text-muted-foreground'>
          Review Radar retrieval output for text queries and CN codes.
        </p>
      </header>

      <section className='rounded border p-4 space-y-4'>
        <div className='flex items-center gap-2'>
          <Button
            type='button'
            variant={mode === 'text' ? 'default' : 'outline'}
            onClick={() => setMode('text')}
          >
            Text query
          </Button>
          <Button
            type='button'
            variant={mode === 'cnCode' ? 'default' : 'outline'}
            onClick={() => setMode('cnCode')}
          >
            CN code
          </Button>
        </div>

        <div className='flex flex-col md:flex-row gap-3'>
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={
              mode === 'text'
                ? 'Enter product/compliance query'
                : 'Enter CN or TARIC code'
            }
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                onSearch()
              }
            }}
          />
          <Button
            type='button'
            onClick={onSearch}
            disabled={isLoading || query.trim().length === 0}
          >
            {isLoading ? 'Searching...' : 'Rerun query'}
          </Button>
        </div>

        {mode === 'text' ? (
          <div className='grid gap-4 rounded border bg-muted/30 p-4 md:grid-cols-2 lg:grid-cols-6'>
            <NumberControl
              id='top-k'
              label='Top K'
              value={topK}
              onChange={setTopK}
              min={1}
              max={500}
              step={1}
              integer
            />
            <NumberControl
              id='retrieval-floor'
              label='Retrieval floor'
              value={retrievalFloor}
              onChange={setRetrievalFloor}
              min={REVIEW_SCORE_MIN}
            />
            <NumberControl
              id='relevance-threshold'
              label='Relevance threshold'
              value={relevanceThreshold}
              onChange={setRelevanceThreshold}
              min={REVIEW_SCORE_MIN}
            />
            <NumberControl
              id='vector-weight'
              label='Vector weight'
              value={vectorWeight}
              onChange={(nextValue) => setVectorWeight(clampUnit(nextValue))}
            />
            <NumberControl
              id='fts-weight'
              label='FTS weight'
              value={1 - vectorWeight}
              onChange={(nextValue) =>
                setVectorWeight(clampUnit(1 - clampUnit(nextValue)))
              }
            />
            <div className='space-y-2'>
              <Label htmlFor='automotive-diagnostics'>
                Automotive diagnostics
              </Label>
              <div className='flex items-center gap-2 pt-2'>
                <Switch
                  id='automotive-diagnostics'
                  checked={automotiveDiagnostics}
                  onCheckedChange={setAutomotiveDiagnostics}
                />
                <span className='text-xs text-muted-foreground'>
                  Use automotive CELEX labels for supervised diagnostics
                </span>
              </div>
            </div>
          </div>
        ) : null}
      </section>

      {activeError ? (
        <p className='text-sm text-destructive'>
          {activeError instanceof Error ? activeError.message : 'Search failed'}
        </p>
      ) : null}

      {mode === 'text' && textResult ? (
        <section className='space-y-6'>
          <div className='flex flex-wrap items-center gap-3 text-sm'>
            <span className='rounded bg-muted px-2 py-1'>
              Retrieval floor: {textResult.retrievalMinScore.toFixed(2)}
            </span>
            <span className='rounded bg-muted px-2 py-1'>
              Relevance threshold: {textResult.relevanceThreshold.toFixed(2)}
            </span>
            <span className='rounded bg-muted px-2 py-1'>
              Vector weight: {textResult.metadata.vectorWeight.toFixed(2)}
            </span>
            <span className='rounded bg-muted px-2 py-1'>
              FTS weight: {textResult.metadata.lexicalWeight.toFixed(2)}
            </span>
            <span
              className='rounded bg-muted px-2 py-1 max-w-full'
              title={textResult.metadata.lexicalQueryText}
            >
              Lexical: {textResult.metadata.lexicalQueryMode}
            </span>
            <span className='rounded bg-muted px-2 py-1'>
              Top K: {textResult.metadata.limit}
            </span>
            <span className='rounded bg-muted px-2 py-1'>
              Retrieved: {textResult.counts.full}
            </span>
            <span className='rounded bg-muted px-2 py-1'>
              Above threshold: {textResult.counts.relevant}
            </span>
          </div>

          <Collapsible
            open={retrievalExplainOpen}
            onOpenChange={setRetrievalExplainOpen}
          >
            <CollapsibleTrigger
              render={<Button type='button' variant='outline' size='sm' />}
            >
                <ChevronDown
                  className={`mr-2 h-4 w-4 transition-transform ${retrievalExplainOpen ? 'rotate-180' : ''}`}
                />
                Retrieval explain
            </CollapsibleTrigger>
            <CollapsibleContent className='mt-3 rounded border p-4 text-sm space-y-2'>
              <p>
                <span className='font-medium'>Vector query:</span>{' '}
                {textResult.metadata.vectorQueryText}
              </p>
              <p>
                <span className='font-medium'>Lexical FTS:</span>{' '}
                {textResult.metadata.lexicalQueryMode} (
                {textResult.metadata.lexicalQueryParser}) —{' '}
                {textResult.metadata.lexicalQueryText || '(empty)'}
              </p>
              <p>
                <span className='font-medium'>Head term:</span>{' '}
                {textResult.metadata.lexicalHeadTerm ?? '(none)'}
              </p>
              <p>
                <span className='font-medium'>Phrases:</span>{' '}
                {textResult.metadata.lexicalPhrases.length > 0
                  ? textResult.metadata.lexicalPhrases.join(', ')
                  : '(none)'}
              </p>
              <p>
                <span className='font-medium'>Source:</span>{' '}
                {textResult.metadata.keywordSource}
                {textResult.metadata.extractedKeywords.length > 0
                  ? ` — ${textResult.metadata.extractedKeywords.join(', ')}`
                  : ''}
              </p>
            </CollapsibleContent>
          </Collapsible>

          {automotiveDiagnostics &&
          textResult.automotiveCoverage &&
          textResult.automotiveDiagnostics ? (
            <section className='space-y-4 rounded border p-4'>
              <h2 className='text-lg font-medium'>Automotive diagnostics</h2>
              <div className='flex flex-wrap items-center gap-3 text-sm'>
                <span className='rounded bg-muted px-2 py-1'>
                  Retrieved: {textResult.automotiveCoverage.retrieved}/
                  {textResult.automotiveCoverage.total}
                </span>
                <span className='rounded bg-muted px-2 py-1'>
                  Missing: {textResult.automotiveCoverage.missingCelexes.length}
                </span>
              </div>

              <ConfusionMatrixCard
                matrix={textResult.automotiveDiagnostics.confusionMatrix}
              />

              <div className='space-y-2'>
                <h3 className='font-medium'>Missing CELEX</h3>
                {textResult.automotiveCoverage.missingCelexes.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>
                    All automotive CELEX labels were retrieved above the active
                    thresholds.
                  </p>
                ) : (
                  <div className='max-h-44 overflow-auto rounded border p-3'>
                    <div className='flex flex-wrap gap-2'>
                      {textResult.automotiveCoverage.missingCelexes.map((celex) => (
                        <span
                          key={celex}
                          className='rounded bg-muted px-2 py-1 font-mono text-xs'
                        >
                          {celex}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <ThresholdMatrixTable
                rows={textResult.automotiveDiagnostics.thresholdMatrix}
              />
              <WeightSensitivityTable
                rows={textResult.automotiveDiagnostics.weightSensitivity}
              />
            </section>
          ) : null}

          <CollapsibleSection
            title='Above threshold'
            open={relevantOpen}
            onOpenChange={setRelevantOpen}
          >
            <TextCompactTable
              rows={relevantItems}
              emptyLabel='No results above the relevance threshold.'
            />
          </CollapsibleSection>

          <CollapsibleSection
            title='Retrieved (top-K)'
            open={fullOpen}
            onOpenChange={setFullOpen}
          >
            <TextFullTable
              rows={textResult.items}
              emptyLabel='No results found for this query.'
            />
          </CollapsibleSection>
        </section>
      ) : null}

      {mode === 'cnCode' && cnResult ? (
        <section className='space-y-2'>
          <div className='text-sm text-muted-foreground'>
            Returned {cnResult.count} compliance scenarios.
          </div>
          <CnTable
            rows={cnResult.items}
            emptyLabel='No results found for this CN code.'
          />
        </section>
      ) : null}
    </div>
  )
}

function TextCompactTable({
  rows,
  emptyLabel,
}: {
  rows: Array<{ celex: string; title: string | null; scope: string | null }>
  emptyLabel: string
}) {
  if (rows.length === 0) {
    return <p className='text-sm text-muted-foreground'>{emptyLabel}</p>
  }
  return (
    <div className='overflow-auto rounded border'>
      <table className='w-full text-sm'>
        <thead className='bg-muted/50 text-left'>
          <tr>
            <th className='px-3 py-2 align-top'>Celex</th>
            <th className='px-3 py-2 align-top'>Title</th>
            <th className='px-3 py-2 align-top'>Scope</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.celex}-${row.scope ?? 'no-scope'}`} className='border-t align-top'>
              <td className='px-3 py-2 font-mono text-xs'>{row.celex}</td>
              <td className='px-3 py-2'>{row.title ?? '-'}</td>
              <td className='px-3 py-2 whitespace-pre-wrap'>{row.scope ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TextFullTable({
  rows,
  emptyLabel,
}: {
  rows: Array<{
    celex: string
    title: string | null
    scope: string | null
    rank: number
    combinedScore: number
    vectorScore: number
    lexicalScore: number
  }>
  emptyLabel: string
}) {
  if (rows.length === 0) {
    return <p className='text-sm text-muted-foreground'>{emptyLabel}</p>
  }
  return (
    <div className='overflow-auto rounded border'>
      <table className='w-full text-sm'>
        <thead className='bg-muted/50 text-left'>
          <tr>
            <th className='px-3 py-2 align-top'>Rank</th>
            <th className='px-3 py-2 align-top'>Fused score</th>
            <th className='px-3 py-2 align-top'>Vector score</th>
            <th className='px-3 py-2 align-top'>TS score</th>
            <th className='px-3 py-2 align-top'>Celex</th>
            <th className='px-3 py-2 align-top'>Title</th>
            <th className='px-3 py-2 align-top'>Scope</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.celex}-${row.rank}`} className='border-t align-top'>
              <td className='px-3 py-2'>{row.rank}</td>
              <td className='px-3 py-2'>{row.combinedScore.toFixed(4)}</td>
              <td className='px-3 py-2'>{row.vectorScore.toFixed(4)}</td>
              <td className='px-3 py-2'>{row.lexicalScore.toFixed(4)}</td>
              <td className='px-3 py-2 font-mono text-xs'>{row.celex}</td>
              <td className='px-3 py-2'>{row.title ?? '-'}</td>
              <td className='px-3 py-2 whitespace-pre-wrap'>{row.scope ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CnTable({
  rows,
  emptyLabel,
}: {
  rows: Array<{ celex: string; title: string | null; scope: string | null }>
  emptyLabel: string
}) {
  return <TextCompactTable rows={rows} emptyLabel={emptyLabel} />
}

function NumberControl({
  id,
  label,
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.01,
  integer = false,
}: {
  id: string
  label: string
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  integer?: boolean
}) {
  return (
    <div className='space-y-2'>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type='number'
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => {
          const raw = Number(event.target.value)
          if (Number.isFinite(raw)) {
            const parsed = integer ? Math.trunc(raw) : raw
            onChange(clampRange(parsed, min, max))
          }
        }}
      />
    </div>
  )
}

function ConfusionMatrixCard({
  matrix,
}: {
  matrix: {
    counts: { tp: number; fp: number; fn: number; tn: number }
    rates: { precision: number | null; recall: number | null; f1: number | null }
  }
}) {
  return (
    <div className='space-y-3'>
      <h3 className='font-medium'>Confusion matrix</h3>
      <div className='grid gap-2 sm:grid-cols-2 lg:grid-cols-4 text-sm'>
        <MetricChip label='TP' value={String(matrix.counts.tp)} />
        <MetricChip label='FP' value={String(matrix.counts.fp)} />
        <MetricChip label='FN' value={String(matrix.counts.fn)} />
        <MetricChip label='TN' value={String(matrix.counts.tn)} />
      </div>
      <div className='grid gap-2 sm:grid-cols-3 text-sm'>
        <MetricChip label='Precision' value={formatRate(matrix.rates.precision)} />
        <MetricChip label='Recall' value={formatRate(matrix.rates.recall)} />
        <MetricChip label='F1' value={formatRate(matrix.rates.f1)} />
      </div>
    </div>
  )
}

function ThresholdMatrixTable({
  rows,
}: {
  rows: Array<{
    retrievalFloor: number
    relevanceThreshold: number
    counts: { tp: number; fp: number; fn: number; tn: number }
    rates: { precision: number | null; recall: number | null; f1: number | null }
  }>
}) {
  return (
    <div className='space-y-2'>
      <h3 className='font-medium'>Threshold sensitivity</h3>
      <p className='text-xs text-muted-foreground'>
        Interpretation note: each row uses an effective decision cutoff of
        max(retrievalFloor, relevanceThreshold), so different floor/threshold
        pairs may produce identical metrics.
      </p>
      <div className='overflow-auto rounded border'>
        <table className='w-full text-sm'>
          <thead className='bg-muted/50 text-left'>
            <tr>
              <th className='px-3 py-2'>Floor</th>
              <th className='px-3 py-2'>Threshold</th>
              <th className='px-3 py-2'>TP</th>
              <th className='px-3 py-2'>FP</th>
              <th className='px-3 py-2'>FN</th>
              <th className='px-3 py-2'>TN</th>
              <th className='px-3 py-2'>Precision</th>
              <th className='px-3 py-2'>Recall</th>
              <th className='px-3 py-2'>F1</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.retrievalFloor}-${row.relevanceThreshold}`}
                className='border-t'
              >
                <td className='px-3 py-2'>{row.retrievalFloor.toFixed(2)}</td>
                <td className='px-3 py-2'>{row.relevanceThreshold.toFixed(2)}</td>
                <td className='px-3 py-2'>{row.counts.tp}</td>
                <td className='px-3 py-2'>{row.counts.fp}</td>
                <td className='px-3 py-2'>{row.counts.fn}</td>
                <td className='px-3 py-2'>{row.counts.tn}</td>
                <td className='px-3 py-2'>{formatRate(row.rates.precision)}</td>
                <td className='px-3 py-2'>{formatRate(row.rates.recall)}</td>
                <td className='px-3 py-2'>{formatRate(row.rates.f1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function WeightSensitivityTable({
  rows,
}: {
  rows: Array<{
    lexicalWeight: number
    vectorWeight: number
    counts: { tp: number; fp: number; fn: number; tn: number }
    rates: { precision: number | null; recall: number | null; f1: number | null }
  }>
}) {
  return (
    <div className='space-y-2'>
      <h3 className='font-medium'>Fused-weight sensitivity</h3>
      <div className='overflow-auto rounded border'>
        <table className='w-full text-sm'>
          <thead className='bg-muted/50 text-left'>
            <tr>
              <th className='px-3 py-2'>FTS weight</th>
              <th className='px-3 py-2'>Vector weight</th>
              <th className='px-3 py-2'>TP</th>
              <th className='px-3 py-2'>FP</th>
              <th className='px-3 py-2'>FN</th>
              <th className='px-3 py-2'>TN</th>
              <th className='px-3 py-2'>Precision</th>
              <th className='px-3 py-2'>Recall</th>
              <th className='px-3 py-2'>F1</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.lexicalWeight}-${row.vectorWeight}`}
                className='border-t'
              >
                <td className='px-3 py-2'>{row.lexicalWeight.toFixed(2)}</td>
                <td className='px-3 py-2'>{row.vectorWeight.toFixed(2)}</td>
                <td className='px-3 py-2'>{row.counts.tp}</td>
                <td className='px-3 py-2'>{row.counts.fp}</td>
                <td className='px-3 py-2'>{row.counts.fn}</td>
                <td className='px-3 py-2'>{row.counts.tn}</td>
                <td className='px-3 py-2'>{formatRate(row.rates.precision)}</td>
                <td className='px-3 py-2'>{formatRate(row.rates.recall)}</td>
                <td className='px-3 py-2'>{formatRate(row.rates.f1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function MetricChip({ label, value }: { label: string; value: string }) {
  return (
    <div className='rounded bg-muted px-3 py-2'>
      <div className='text-xs text-muted-foreground'>{label}</div>
      <div className='text-sm font-medium'>{value}</div>
    </div>
  )
}

function CollapsibleSection({
  title,
  open,
  onOpenChange,
  children,
}: {
  title: string
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}) {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className='space-y-2'>
      <CollapsibleTrigger
        render={
          <Button
            type='button'
            variant='outline'
            className='flex w-full items-center justify-between'
          />
        }
      >
          <span className='text-lg font-medium'>{title}</span>
          <ChevronDown
            className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
          />
      </CollapsibleTrigger>
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  )
}

function clampUnit(value: number): number {
  return Math.min(1, Math.max(0, value))
}

function clampRange(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function formatRate(value: number | null): string {
  if (value == null) {
    return '-'
  }
  return value.toFixed(4)
}
